import React, { useEffect, useState, useRef } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaPlus, FaMagic, FaTrash, FaTimes, FaPlay, FaPause, FaVideo,
  FaCheckCircle, FaSearch, FaBed, FaBath, FaCar, FaDownload,
  FaChevronRight, FaMusic, FaRobot, FaPen, FaArrowLeft, FaEdit,
  FaArrowUp, FaArrowDown, FaShareAlt, FaCopy, FaCalendarAlt, FaFacebook,
} from "react-icons/fa";
import EmptyFeatureState from "../components/EmptyFeatureState";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);
  return matches;
}

function StatusBadge({ status }) {
  const map = {
    completed: { bg: "#d1fae5", color: "#065f46", label: "Completed" },
    generating: { bg: "#dbeafe", color: "#1d4ed8", label: "Generating" },
    failed: { bg: "#fee2e2", color: "#991b1b", label: "Failed" },
  };
  const s = map[status] || map.completed;
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
      padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

export default function AIVideo() {
  const isMobile = !useMediaQuery("(min-width: 768px)");

  // List view state
  const [videos, setVideos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState("input"); // input | preview | manual_select | ai_loading | review | generating | done

  // Input state
  const [listingId, setListingId] = useState("");
  const [maxImages, setMaxImages] = useState(8);
  const [musicStyle, setMusicStyle] = useState("cinematic");
  const [musicQuery, setMusicQuery] = useState("");
  const [musicResults, setMusicResults] = useState([]);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioRef = useRef(null);
  const [listingPreview, setListingPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  // Mode + review state
  const [selectedMode, setSelectedMode] = useState(null); // "ai" | "manual"
  const [selectedImages, setSelectedImages] = useState([]); // manual mode: selected image URLs
  const [reviewSlides, setReviewSlides] = useState([]); // slides for review: [{image_url, headline, subtitle}]
  const [aiLoading, setAiLoading] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [slides, setSlides] = useState([]);
  const progressTimer = useRef(null);

  // Result state
  const [videoUrl, setVideoUrl] = useState("");
  const [resultDetails, setResultDetails] = useState(null);

  // Player modal
  const [playerModal, setPlayerModal] = useState({ open: false, url: "", title: "" });

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Social Post state
  const [socialPostModal, setSocialPostModal] = useState({ open: false, videoId: null });
  const [socialPostText, setSocialPostText] = useState("");
  const [socialPostLoading, setSocialPostLoading] = useState(false);
  const [socialPostPrompt, setSocialPostPrompt] = useState("");
  const [copyFlash, setCopyFlash] = useState(false);
  const socialPostVideoRef = useRef(null);
  const socialPostPromptRef = useRef(null);
  const socialPostTextRef = useRef(null);
  const [fbConnected, setFbConnected] = useState(false);
  const [fbPages, setFbPages] = useState([]);
  const [fbSelectedPage, setFbSelectedPage] = useState("");
  const [fbPosted, setFbPosted] = useState([]);
  const [fbPosting, setFbPosting] = useState(false);
  const [fbPostMsg, setFbPostMsg] = useState("");
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [showScheduleFields, setShowScheduleFields] = useState(false);

  const defaultBlurbPrompt = `You are a social media copywriter for a real estate agent. Your job is to write a short, shareable social media blurb that entices people to watch a property listing video.

Rules:
- The blurb should be 4-6 short lines/paragraphs max.
- Open with a punchy, attention-grabbing headline that references the property location. Use an emoji.
- Follow with 1-2 sentences highlighting the property's appeal — mention the price if available, the location, and create excitement about the video walkthrough.
- Then add a short punchy line — think three-word phrases separated by full stops, or a bold takeaway.
- End with a call-to-action line encouraging the reader to watch the video. Use an emoji like 👇 or 🎥.
- Do NOT include any URLs or links — the video will be attached to the post automatically.
- Tone: confident, conversational, slightly urgent. Not salesy or formal.
- Do NOT use hashtags.
- Do NOT use em dashes.
- Return only the blurb text, no explanation or markdown formatting.`;

  // Auto-resize the social post prompt textarea
  useEffect(() => {
    const el = socialPostPromptRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  }, [socialPostPrompt]);

  // Auto-resize the social post text textarea
  useEffect(() => {
    const el = socialPostTextRef.current;
    if (el) {
      el.style.height = "0px";
      el.style.height = Math.max(200, el.scrollHeight) + "px";
    }
  }, [socialPostText, socialPostLoading]);

  // Check Facebook connection status
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const email = (user?.agent_details?.agent_email || "").toLowerCase();
      if (email !== "bayan@vendorleads.com.au") return;
    } catch { return; }
    handleTokenAndPostRequest(`${window.api_host}/facebook-status`)
      .then((res) => {
        if (res.data?.connected) {
          setFbConnected(true);
          setFbPages(res.data.pages || []);
          if (res.data.pages?.length) setFbSelectedPage(res.data.pages[0].page_id);
          setFbPosted(res.data.posted || []);
        }
      })
      .catch(() => {});
    handleTokenAndPostRequest(`${window.api_host}/scheduled-posts`)
      .then((res) => {
        if (res.data?.posts) setScheduledPosts(res.data.posts.filter(p => p.status === "pending"));
      })
      .catch(() => {});
  }, []);

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 4000);
  }

  // Load video list
  async function loadVideos(silent = false) {
    if (!silent) setLoadingList(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/list-ai-videos`, {}
      );
      if (res.data?.videos) setVideos(res.data.videos);
    } catch {
      if (!silent) showToast("Failed to load videos.", "error");
    }
    if (!silent) setLoadingList(false);
  }

  useEffect(() => { loadVideos(); }, []);

  // Reset modal
  function resetModal() {
    setListingId("");
    setMaxImages(8);
    setMusicStyle("cinematic");
    setListingPreview(null);
    setLoadingPreview(false);
    setPreviewError("");
    setModalStep("input");
    setGenerating(false);
    setProgress(0);
    setProgressMessage("");
    setSlides([]);
    setVideoUrl("");
    setResultDetails(null);
    setSelectedMode(null);
    setSelectedImages([]);
    setReviewSlides([]);
    setAiLoading(false);
    setMusicQuery("");
    setMusicResults([]);
    setLoadingMusic(false);
    setSelectedTrack(null);
    setPlayingTrackId(null);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
  }

  function openModal() {
    resetModal();
    setModalOpen(true);
  }

  function closeModal() {
    if (generating) return;
    setModalOpen(false);
    if (videoUrl) loadVideos();
  }

  // Fetch listing preview
  async function handleLookup() {
    if (!listingId.trim()) return;
    setLoadingPreview(true);
    setPreviewError("");
    setListingPreview(null);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/get-listing-preview`,
        { listing_id: listingId.trim() }
      );
      if (res.data?.status === "success") {
        setListingPreview(res.data.listing);
        setModalStep("preview");
      } else {
        setPreviewError(res.data?.message || "Listing not found.");
      }
    } catch {
      setPreviewError("Failed to fetch listing. Check the ID and try again.");
    }
    setLoadingPreview(false);
  }

  // Progress helpers
  function startProgress() {
    setProgress(0);
    const startTime = Date.now();
    const DURATION = 120000; // 2 min for video
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      setProgress(Math.min(92, ratio * 100 * (2 - ratio)));
    }, 300);
  }
  function stopProgress() {
    if (progressTimer.current) clearInterval(progressTimer.current);
    setProgress(100);
    setTimeout(() => setProgress(0), 500);
  }
  useEffect(() => () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
  }, []);

  // AI mode: call prepare endpoint, then show review
  async function handleAIPrepare() {
    setAiLoading(true);
    setModalStep("ai_loading");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/prepare-ai-video-slides`,
        { listing_id: listingId.trim(), max_images: maxImages }
      );
      if (res.data?.status === "success" && res.data.slides) {
        setReviewSlides(res.data.slides);
        setModalStep("review");
      } else {
        showToast(res.data?.message || "Failed to prepare slides.", "error");
        setModalStep("preview");
      }
    } catch {
      showToast("Failed to prepare slides.", "error");
      setModalStep("preview");
    }
    setAiLoading(false);
  }

  // Manual mode: go to review with user-selected images
  function handleManualContinue() {
    if (selectedImages.length === 0) return;
    const slides = selectedImages.map((url, i) => ({
      image_url: url,
      headline: i === 0 ? (listingPreview?.address || "") : "",
      subtitle: "",
    }));
    setReviewSlides(slides);
    setModalStep("review");
  }

  // Toggle image selection in manual mode
  function toggleImageSelection(url) {
    setSelectedImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  }

  // Update a slide's text in review
  function updateSlideText(index, field, value) {
    setReviewSlides((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  }

  // Remove a slide in review
  function removeSlide(index) {
    setReviewSlides((prev) => prev.filter((_, i) => i !== index));
  }

  // Reorder slides
  function moveSlide(index, direction) {
    setReviewSlides((prev) => {
      const arr = [...prev];
      const target = index + direction;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  }

  // AI generate titles for existing review slides
  async function handleAIGenerateTitles() {
    setAiLoading(true);
    try {
      const imageUrls = reviewSlides.map((s) => s.image_url);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/generate-ai-titles`,
        { listing_id: listingId.trim(), image_urls: imageUrls }
      );
      if (res.data?.status === "success" && res.data.titles) {
        setReviewSlides((prev) =>
          prev.map((s, i) => ({
            ...s,
            headline: res.data.titles[i]?.headline || s.headline,
            subtitle: res.data.titles[i]?.subtitle || s.subtitle,
          }))
        );
        showToast("AI titles generated — review and edit as needed.");
      } else {
        showToast(res.data?.message || "Failed to generate titles.", "error");
      }
    } catch {
      showToast("Failed to generate titles.", "error");
    }
    setAiLoading(false);
  }

  // Music search + preview
  async function handleMusicSearch(q) {
    const query = q || musicQuery;
    if (!query?.trim()) return;
    setLoadingMusic(true);
    setMusicResults([]);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/search-music`,
        { query: query.trim() }
      );
      if (res.data?.status === "success") {
        setMusicResults(res.data.tracks || []);
      } else {
        showToast(res.data?.message || "Music search failed.", "error");
      }
    } catch {
      showToast("Failed to search music.", "error");
    }
    setLoadingMusic(false);
  }

  function togglePlayTrack(track) {
    if (playingTrackId === track.id) {
      audioRef.current?.pause();
      setPlayingTrackId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = track.preview_url;
        audioRef.current.play().catch(() => {});
      }
      setPlayingTrackId(track.id);
    }
  }

  // Generate video (SSE) — now accepts reviewed slides
  async function handleGenerate() {
    setGenerating(true);
    setModalStep("generating");
    setSlides([]);
    setVideoUrl("");
    startProgress();

    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { "Content-Type": "application/json" };
      if (jwt_token) headers["Authorization"] = `Bearer ${jwt_token}`;

      const body = {
        listing_id: listingId.trim(),
        max_images: maxImages,
        music_style: musicStyle,
        music_url: selectedTrack?.preview_url || null,
      };
      // Pass reviewed slides so backend skips AI steps
      if (reviewSlides.length > 0) {
        body.slides = reviewSlides;
      }

      const response = await fetch(`${window.api_host}/generate-ai-video`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        showToast("Failed to generate video.", "error");
        stopProgress();
        setGenerating(false);
        setModalStep("review");
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();

        for (const event of events) {
          const dataLine = event.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === "progress") {
              setProgressMessage(parsed.message || "");
              if (parsed.slides) setSlides(parsed.slides);
            } else if (parsed.type === "done") {
              setVideoUrl(parsed.video_url);
              setResultDetails({
                address: parsed.listing_address,
                price: parsed.display_price,
              });
              setModalStep("done");
            } else if (parsed.type === "error") {
              showToast(parsed.message || "Failed to generate video.", "error");
              setModalStep("review");
            }
          } catch {
            // skip malformed events
          }
        }
      }
    } catch {
      showToast("Failed to generate video.", "error");
      setModalStep("review");
    }
    stopProgress();
    setGenerating(false);
  }

  // Delete video
  async function handleDelete() {
    if (!deleteModal.id) return;
    setDeleting(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-ai-video`,
        { video_id: deleteModal.id }
      );
      setVideos((prev) => prev.filter((v) => v.id !== deleteModal.id));
      showToast("Video deleted.");
    } catch {
      showToast("Failed to delete video.", "error");
    }
    setDeleting(false);
    setDeleteModal({ open: false, id: null });
  }

  // Social Post handlers
  async function handleSocialPost(video, existingScheduled) {
    socialPostVideoRef.current = video;
    setSocialPostModal({ open: true, videoId: video.id });
    setSocialPostText("");
    setSocialPostPrompt(defaultBlurbPrompt);
    setSocialPostLoading(true);
    setFbPostMsg("");
    if (existingScheduled) {
      setShowScheduleFields(true);
      const dt = new Date(existingScheduled.scheduled_at);
      setScheduleDate(dt.toISOString().split('T')[0]);
      setScheduleTime(dt.toTimeString().slice(0, 5));
    } else {
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
    }
    if (existingScheduled) {
      setSocialPostText(existingScheduled.message || "");
      setSocialPostLoading(false);
      return;
    }
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/video-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ address: video.listing_address, price: video.display_price, blurb_prompt: defaultBlurbPrompt, stream: true }),
      });
      if (!resp.ok) { setSocialPostText("Failed to generate social post."); setSocialPostLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialPostText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialPostText("Failed to generate social post.");
    } catch {
      setSocialPostText("Failed to generate social post.");
    }
    setSocialPostLoading(false);
  }

  async function handleRegenerateBlurb() {
    const video = socialPostVideoRef.current;
    if (!video) return;
    setSocialPostText("");
    setSocialPostLoading(true);
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/video-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ address: video.listing_address, price: video.display_price, blurb_prompt: socialPostPrompt, stream: true }),
      });
      if (!resp.ok) { setSocialPostText("Failed to generate social post."); setSocialPostLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialPostText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialPostText("Failed to generate social post.");
    } catch {
      setSocialPostText("Failed to generate social post.");
    }
    setSocialPostLoading(false);
  }

  async function handleScheduleVideo() {
    if (!scheduleDate || !scheduleTime || !socialPostText.trim() || !fbSelectedPage) return;
    setScheduleSaving(true);
    try {
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      const videoId = socialPostModal.videoId;
      const existing = scheduledPosts.find(p => p.asset_type === "video" && p.asset_id === String(videoId));
      if (existing && existing.id) {
        await handleTokenAndPostRequest(
          `${window.api_host}/update-scheduled-post`,
          { id: existing.id, message: socialPostText, scheduled_at: scheduledAt, page_id: fbSelectedPage }
        );
        setScheduledPosts(prev => prev.map(p =>
          p.id === existing.id ? { ...p, message: socialPostText, scheduled_at: scheduledAt, page_id: fbSelectedPage } : p
        ));
        showToast("Schedule updated!");
      } else {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/schedule-social-post`,
          { asset_type: "video", asset_id: String(videoId), page_id: fbSelectedPage, message: socialPostText, scheduled_at: scheduledAt }
        );
        setScheduledPosts(prev => [...prev, { id: res.data?.id, asset_type: "video", asset_id: String(videoId), scheduled_at: scheduledAt, status: "pending", message: socialPostText, page_id: fbSelectedPage }]);
        showToast("Post scheduled!");
      }
      setSocialPostModal({ open: false, videoId: null });
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
    } catch {
      showToast("Failed to schedule post.", "error");
    }
    setScheduleSaving(false);
  }

  // ─────────────── RENDER ───────────────

  return (
    <div style={{
      flex: 1, overflowY: "auto", padding: isMobile ? "72px 12px 24px" : "32px 40px",
      maxWidth: 960, margin: "0 auto", width: "100%", boxSizing: "border-box",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 24,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937" }}>
            AI Video
          </h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
            Generate premium property listing videos with AI-selected images and text.
          </div>
        </div>
        <button
          onClick={openModal}
          style={{
            background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8,
            padding: "10px 20px", fontWeight: 600, cursor: "pointer", fontSize: 14,
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <FaPlus style={{ fontSize: 12 }} /> Generate Video
        </button>
      </div>

      {/* Video list */}
      {loadingList ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16, overflow: "hidden",
              boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
              display: "flex", flexDirection: isMobile ? "column" : "row",
            }}>
              <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 160 : 140, position: "relative" }}>
                <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 160 : 140 }} />
              </div>
              <div style={{ flex: 1, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton width="70%" height={16} />
                <Skeleton width="30%" height={12} />
                <div style={{ display: "flex", gap: 6 }}>
                  <Skeleton width={60} height={18} borderRadius={10} />
                  <Skeleton width={70} height={18} borderRadius={10} />
                </div>
                <Skeleton width="90%" height={12} />
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <Skeleton width={60} height={28} borderRadius={8} />
                  <Skeleton width={80} height={28} borderRadius={8} />
                  <Skeleton width={60} height={28} borderRadius={8} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : videos.length === 0 ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyFeatureState
          icon={<FaVideo />}
          title="No Videos Yet"
          description="Turn any property listing into a polished video in minutes. AI selects the best photos, writes captions, and adds music — ready to share on social media or send to vendors."
          features={[
            { icon: <FaMagic />, text: "AI picks the best listing photos and writes professional captions" },
            { icon: <FaMusic />, text: "Choose from curated background music tracks to set the mood" },
            { icon: <FaShareAlt />, text: "Download or share directly to Facebook and Instagram" },
          ]}
          examplePreview={
            <div style={{ padding: 16, display: "flex", gap: 12, flexDirection: isMobile ? "column" : "row" }}>
              {[
                { addr: "42 Ocean View Drive, Coogee", beds: 4, baths: 2, status: "Ready" },
                { addr: "15/88 Marine Parade, Maroubra", beds: 3, baths: 2, status: "Ready" },
              ].map((v) => (
                <div key={v.addr} style={{
                  flex: 1, background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: 80, background: "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <FaPlay style={{ color: "#fff", fontSize: 20, opacity: 0.8 }} />
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", marginBottom: 4 }}>{v.addr}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{v.beds} bed · {v.baths} bath</div>
                    <div style={{
                      marginTop: 8, display: "inline-block", fontSize: 10, fontWeight: 600,
                      background: "#ecfdf5", color: "#059669", padding: "2px 8px", borderRadius: 999,
                    }}>{v.status}</div>
                  </div>
                </div>
              ))}
            </div>
          }
          ctaLabel="Generate Your First Video"
          onCta={openModal}
          isMobile={isMobile}
        />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {videos.map((v) => (
            <div
              key={v.id}
              style={{
                background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                padding: 0, display: "flex", overflow: "hidden",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              {/* Thumbnail */}
              <div
                onClick={() => v.video_url && setPlayerModal({ open: true, url: v.video_url, title: v.listing_address })}
                style={{
                  width: isMobile ? "100%" : 200, minHeight: isMobile ? 160 : 140,
                  background: `url(${v.thumbnail_url}) center/cover no-repeat`,
                  backgroundColor: "#f3f4f6",
                  cursor: v.video_url ? "pointer" : "default",
                  position: "relative", flexShrink: 0,
                }}
              >
                {v.video_url && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.3)", transition: "background 0.2s",
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: "50%",
                      background: "rgba(255,255,255,0.9)", display: "flex",
                      alignItems: "center", justifyContent: "center",
                    }}>
                      <FaPlay style={{ fontSize: 16, color: "#7c3aed", marginLeft: 2 }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Details */}
              <div style={{
                flex: 1, padding: "18px 22px",
                display: "flex", flexDirection: "column", justifyContent: "center",
                minWidth: 0,
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  flexWrap: "wrap", marginBottom: 4,
                }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
                    {v.listing_address || "Property Video"}
                  </span>
                  <StatusBadge status={v.status} />
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {v.display_price && <span>{v.display_price}</span>}
                  {v.image_count && <span>{v.image_count} slides</span>}
                  {v.created_at && (
                    <span>
                      {new Date(v.created_at * 1000).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div style={{
                  display: "flex", gap: 8, alignItems: "center",
                  flexWrap: "wrap", marginTop: 10,
                }}>
                  {v.video_url && (
                    <a
                      href={v.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      style={{
                        background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe",
                        borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                        cursor: "pointer", fontSize: 12, textDecoration: "none",
                        display: "flex", alignItems: "center", gap: 5,
                      }}
                    >
                      <FaDownload style={{ fontSize: 10 }} /> Download
                    </a>
                  )}
                  {(() => {
                    const posted = fbPosted.some(p => p.asset_type === "video" && p.asset_id === String(v.id) && p.page_id === fbSelectedPage);
                    const scheduled = scheduledPosts.find(p => p.asset_type === "video" && p.asset_id === String(v.id));
                    return (
                      <>
                        <button
                          onClick={() => !posted && handleSocialPost(v, scheduled || null)}
                          disabled={posted}
                          style={{
                            background: posted ? "#d1d5db" : scheduled ? "#f5f3ff" : "#f3f4f6",
                            color: posted ? "#6b7280" : scheduled ? "#6d28d9" : "#374151",
                            border: scheduled && !posted ? "1px solid #ddd6fe" : "none",
                            borderRadius: 8,
                            padding: "6px 14px",
                            fontWeight: 600,
                            cursor: posted ? "not-allowed" : "pointer",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <FaShareAlt style={{ fontSize: 11 }} />
                          {posted ? "Already Posted" : scheduled ? "Edit Schedule" : "Social Post"}
                        </button>
                        {scheduled && (
                          <span style={{
                            fontSize: 11,
                            color: "#7c3aed",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}>
                            <FaCalendarAlt style={{ fontSize: 10 }} />
                            {new Date(scheduled.scheduled_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                            {" "}
                            {new Date(scheduled.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <button
                    onClick={() => setDeleteModal({ open: true, id: v.id })}
                    style={{
                      background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                      borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                      cursor: "pointer", fontSize: 12, display: "flex",
                      alignItems: "center", gap: 5,
                    }}
                  >
                    <FaTrash style={{ fontSize: 10 }} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Generate Modal ─── */}
      {modalOpen && (
        <div
          style={{
            position: "fixed", top: 0, left: isMobile ? 0 : 260, right: 0, bottom: 0,
            background: "#fff", zIndex: 1000,
            display: "flex", flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 0,
              width: "100%", height: "100%",
              overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Modal header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "18px 24px", borderBottom: "1px solid #f3f4f6", flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaVideo style={{ fontSize: 18, color: "#7c3aed" }} />
                <span style={{ fontWeight: 700, fontSize: 17, color: "#1a1a1a" }}>
                  {modalStep === "done" ? "Video Ready" : "Generate Property Video"}
                </span>
              </div>
              <button
                onClick={closeModal}
                disabled={generating}
                style={{
                  background: "none", border: "none", cursor: generating ? "not-allowed" : "pointer",
                  fontSize: 18, color: "#9ca3af", padding: 4, lineHeight: 1,
                }}
              ><FaTimes /></button>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

              {/* ── Step: Input ── */}
              {modalStep === "input" && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                    Domain Listing ID
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10, lineHeight: 1.4 }}>
                    Enter the Domain listing ID to generate a video.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={listingId}
                      onChange={(e) => { setListingId(e.target.value); setPreviewError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                      placeholder="e.g. 2019876543"
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 8,
                        border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box",
                      }}
                    />
                    <button
                      onClick={handleLookup}
                      disabled={loadingPreview || !listingId.trim()}
                      style={{
                        background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8,
                        padding: "10px 18px", fontWeight: 600, fontSize: 13,
                        cursor: loadingPreview || !listingId.trim() ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 6,
                        opacity: !listingId.trim() ? 0.5 : 1,
                      }}
                    >
                      {loadingPreview
                        ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                        : <FaSearch style={{ fontSize: 12 }} />}
                      Lookup
                    </button>
                  </div>
                  {previewError && (
                    <div style={{
                      fontSize: 12, color: "#dc2626", marginTop: 8,
                      padding: "8px 12px", background: "#fef2f2", borderRadius: 8,
                    }}>
                      {previewError}
                    </div>
                  )}
                </div>
              )}

              {/* ── Step: Preview (mode selection) ── */}
              {modalStep === "preview" && listingPreview && (
                <div style={{ maxWidth: 640, margin: "0 auto" }}>
                  {/* Listing card */}
                  <div style={{
                    display: "flex", gap: 14, marginBottom: 24,
                    background: "#f9fafb", borderRadius: 12, padding: 16,
                    border: "1px solid #e5e7eb",
                  }}>
                    {listingPreview.thumbnail && (
                      <div style={{
                        width: 100, height: 75, borderRadius: 8, overflow: "hidden",
                        flexShrink: 0,
                        background: `url(${listingPreview.thumbnail}) center/cover no-repeat`,
                        backgroundColor: "#e5e7eb",
                      }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a", marginBottom: 4 }}>
                        {listingPreview.address}
                      </div>
                      <div style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600, marginBottom: 6 }}>
                        {listingPreview.price}
                      </div>
                      <div style={{ display: "flex", gap: 12, fontSize: 12, color: "#6b7280" }}>
                        {listingPreview.bedrooms != null && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <FaBed style={{ fontSize: 11 }} /> {listingPreview.bedrooms}
                          </span>
                        )}
                        {listingPreview.bathrooms != null && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <FaBath style={{ fontSize: 11 }} /> {listingPreview.bathrooms}
                          </span>
                        )}
                        {listingPreview.carspaces != null && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <FaCar style={{ fontSize: 11 }} /> {listingPreview.carspaces}
                          </span>
                        )}
                        <span>{listingPreview.property_type}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {listingPreview.image_count} images available
                      </div>
                    </div>
                  </div>

                  {/* Mode selection */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 12 }}>
                    How would you like to create your video?
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
                    <button
                      onClick={() => { setSelectedMode("ai"); }}
                      style={{
                        flex: 1, minWidth: 200, background: selectedMode === "ai" ? "#f5f3ff" : "#fff",
                        border: selectedMode === "ai" ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                        borderRadius: 12, padding: 20, cursor: "pointer", textAlign: "left",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <FaRobot style={{ fontSize: 18, color: "#7c3aed" }} />
                        <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>AI Select</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                        AI selects the best images and writes engaging overlay text for each slide.
                      </div>
                    </button>
                    <button
                      onClick={() => { setSelectedMode("manual"); }}
                      style={{
                        flex: 1, minWidth: 200, background: selectedMode === "manual" ? "#f5f3ff" : "#fff",
                        border: selectedMode === "manual" ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                        borderRadius: 12, padding: 20, cursor: "pointer", textAlign: "left",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <FaPen style={{ fontSize: 16, color: "#7c3aed" }} />
                        <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>Manual Select</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                        Choose images and write your own text for each slide in the video.
                      </div>
                    </button>
                  </div>

                  {/* AI mode options */}
                  {selectedMode === "ai" && (
                    <>
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                          Max Images in Video
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {[4, 6, 8, 10, 12].map((n) => {
                            const totalImages = listingPreview?.image_count || 0;
                            const disabled = n > totalImages;
                            return (
                              <button
                                key={n}
                                onClick={() => !disabled && setMaxImages(n)}
                                disabled={disabled}
                                style={{
                                  background: maxImages === n ? "#ede9fe" : "#f3f4f6",
                                  color: disabled ? "#c0c0c0" : maxImages === n ? "#6d28d9" : "#374151",
                                  border: maxImages === n ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
                                  borderRadius: 8, padding: "6px 14px", fontSize: 13,
                                  fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
                                  opacity: disabled ? 0.5 : 1,
                                }}
                              >{n}</button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => { setModalStep("input"); setListingPreview(null); setSelectedMode(null); }}
                      style={{
                        background: "#f3f4f6", color: "#374151", border: "none",
                        borderRadius: 8, padding: "10px 18px", fontWeight: 600,
                        cursor: "pointer", fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    ><FaArrowLeft style={{ fontSize: 11 }} /> Back</button>
                    {selectedMode === "ai" && (
                      <button
                        onClick={handleAIPrepare}
                        style={{
                          flex: 1, background: "#7c3aed", color: "#fff", border: "none",
                          borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                          cursor: "pointer", fontSize: 14,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        }}
                      >
                        <FaRobot style={{ fontSize: 13 }} /> AI Select Images & Text
                      </button>
                    )}
                    {selectedMode === "manual" && (
                      <button
                        onClick={() => { setSelectedImages([]); setModalStep("manual_select"); }}
                        style={{
                          flex: 1, background: "#7c3aed", color: "#fff", border: "none",
                          borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                          cursor: "pointer", fontSize: 14,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        }}
                      >
                        <FaPen style={{ fontSize: 12 }} /> Select Images
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Step: AI Loading ── */}
              {modalStep === "ai_loading" && (
                <div style={{ textAlign: "center", padding: "40px 20px", maxWidth: 480, margin: "0 auto" }}>
                  <Spinner animation="border" style={{ width: 40, height: 40, color: "#7c3aed", marginBottom: 16 }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>
                    AI is preparing your slides…
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                    Selecting the best images and generating text. This usually takes 10–20 seconds.
                  </div>
                </div>
              )}

              {/* ── Step: Manual Select ── */}
              {modalStep === "manual_select" && listingPreview && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>
                    Select images for your video
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                    Click images to select them. They will appear in the order you select them. ({selectedImages.length} selected)
                  </div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 10, marginBottom: 20,
                  }}>
                    {(listingPreview.all_image_urls || []).map((url, i) => {
                      const isSelected = selectedImages.includes(url);
                      const selIndex = selectedImages.indexOf(url);
                      return (
                        <div
                          key={i}
                          onClick={() => toggleImageSelection(url)}
                          style={{
                            position: "relative", borderRadius: 10, overflow: "hidden",
                            cursor: "pointer", aspectRatio: "4/3",
                            border: isSelected ? "3px solid #7c3aed" : "2px solid #e5e7eb",
                            opacity: isSelected ? 1 : 0.7,
                            transition: "all 0.15s",
                          }}
                        >
                          <div style={{
                            width: "100%", height: "100%",
                            background: `url(${url}) center/cover no-repeat`,
                            backgroundColor: "#e5e7eb",
                          }} />
                          {isSelected && (
                            <div style={{
                              position: "absolute", top: 6, left: 6,
                              width: 24, height: 24, borderRadius: "50%",
                              background: "#7c3aed", color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 700,
                            }}>
                              {selIndex + 1}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => { setModalStep("preview"); setSelectedImages([]); }}
                      style={{
                        background: "#f3f4f6", color: "#374151", border: "none",
                        borderRadius: 8, padding: "10px 18px", fontWeight: 600,
                        cursor: "pointer", fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    ><FaArrowLeft style={{ fontSize: 11 }} /> Back</button>
                    <button
                      onClick={handleManualContinue}
                      disabled={selectedImages.length === 0}
                      style={{
                        flex: 1, background: "#7c3aed", color: "#fff", border: "none",
                        borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                        cursor: selectedImages.length === 0 ? "not-allowed" : "pointer",
                        fontSize: 14, opacity: selectedImages.length === 0 ? 0.5 : 1,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      }}
                    >
                      Continue with {selectedImages.length} image{selectedImages.length !== 1 ? "s" : ""} <FaChevronRight style={{ fontSize: 11 }} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step: Review & Edit ── */}
              {modalStep === "review" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                        Review & Edit Slides
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        Edit the headline and subtitle for each slide before generating.
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
                      {reviewSlides.length} slides
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                    {reviewSlides.map((slide, i) => (
                      <div key={i} style={{
                        display: "flex", gap: 12, background: "#f9fafb",
                        borderRadius: 12, padding: 12, border: "1px solid #e5e7eb",
                        alignItems: "flex-start",
                      }}>
                        {/* Thumbnail */}
                        <div style={{
                          width: 120, height: 80, borderRadius: 8, overflow: "hidden",
                          flexShrink: 0,
                          background: `url(${slide.image_url}) center/cover no-repeat`,
                          backgroundColor: "#e5e7eb",
                          position: "relative",
                        }}>
                          <div style={{
                            position: "absolute", top: 4, left: 4,
                            width: 20, height: 20, borderRadius: "50%",
                            background: "#7c3aed", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 700,
                          }}>
                            {i + 1}
                          </div>
                        </div>
                        {/* Text inputs */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input
                            value={slide.headline}
                            onChange={(e) => updateSlideText(i, "headline", e.target.value)}
                            placeholder="Headline (max 6 words)"
                            maxLength={60}
                            style={{
                              width: "100%", padding: "7px 10px", borderRadius: 6,
                              border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600,
                              marginBottom: 6, boxSizing: "border-box",
                            }}
                          />
                          <input
                            value={slide.subtitle}
                            onChange={(e) => updateSlideText(i, "subtitle", e.target.value)}
                            placeholder="Subtitle (max 12 words)"
                            maxLength={100}
                            style={{
                              width: "100%", padding: "7px 10px", borderRadius: 6,
                              border: "1px solid #e5e7eb", fontSize: 12, color: "#374151",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        {/* Reorder + Remove */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                          <button
                            onClick={() => moveSlide(i, -1)}
                            disabled={i === 0}
                            style={{
                              background: "none", border: "none", cursor: i === 0 ? "default" : "pointer",
                              color: i === 0 ? "#d1d5db" : "#6b7280", fontSize: 12, padding: 4,
                            }}
                            title="Move up"
                          ><FaArrowUp /></button>
                          <button
                            onClick={() => moveSlide(i, 1)}
                            disabled={i === reviewSlides.length - 1}
                            style={{
                              background: "none", border: "none",
                              cursor: i === reviewSlides.length - 1 ? "default" : "pointer",
                              color: i === reviewSlides.length - 1 ? "#d1d5db" : "#6b7280",
                              fontSize: 12, padding: 4,
                            }}
                            title="Move down"
                          ><FaArrowDown /></button>
                          {reviewSlides.length > 1 && (
                            <button
                              onClick={() => removeSlide(i)}
                              style={{
                                background: "none", border: "none", cursor: "pointer",
                                color: "#9ca3af", fontSize: 12, padding: 4,
                              }}
                              title="Remove slide"
                            ><FaTimes /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => {
                        setReviewSlides([]);
                        setModalStep(selectedMode === "manual" ? "manual_select" : "preview");
                      }}
                      style={{
                        background: "#f3f4f6", color: "#374151", border: "none",
                        borderRadius: 8, padding: "10px 18px", fontWeight: 600,
                        cursor: "pointer", fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    ><FaArrowLeft style={{ fontSize: 11 }} /> Back</button>
                    <button
                      onClick={handleAIGenerateTitles}
                      disabled={aiLoading || reviewSlides.length === 0}
                      style={{
                        background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe",
                        borderRadius: 8, padding: "10px 18px", fontWeight: 600,
                        cursor: aiLoading || reviewSlides.length === 0 ? "not-allowed" : "pointer",
                        fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6,
                        opacity: aiLoading ? 0.6 : 1,
                      }}
                    >
                      {aiLoading
                        ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                        : <FaRobot style={{ fontSize: 13 }} />}
                      AI Generate Titles
                    </button>
                    <button
                      onClick={() => setModalStep("music")}
                      disabled={reviewSlides.length === 0}
                      style={{
                        flex: 1, background: "#7c3aed", color: "#fff", border: "none",
                        borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                        cursor: reviewSlides.length === 0 ? "not-allowed" : "pointer",
                        fontSize: 14,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      }}
                    >
                      <FaMusic style={{ fontSize: 13 }} /> Choose Music & Build
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step: Music ── */}
              {modalStep === "music" && (
                <div style={{ maxWidth: 640, margin: "0 auto" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>
                    Choose Background Music
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                    Search for a track, preview it, then select one for your video.
                  </div>

                  {/* Genre pills */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                    {[
                      { label: "Cinematic", emoji: "🎬", query: "cinematic background music" },
                      { label: "Piano", emoji: "🎹", query: "elegant piano background" },
                      { label: "Corporate", emoji: "💼", query: "corporate motivational music" },
                      { label: "Chill", emoji: "🌊", query: "chill ambient lofi" },
                      { label: "Upbeat", emoji: "⚡", query: "upbeat positive energy" },
                    ].map((s) => (
                      <button
                        key={s.label}
                        onClick={() => { setMusicQuery(s.query); handleMusicSearch(s.query); }}
                        style={{
                          background: "#f3f4f6", color: "#374151",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8, padding: "6px 14px", fontSize: 12,
                          fontWeight: 600, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 5,
                        }}
                      >
                        <span style={{ fontSize: 13 }}>{s.emoji}</span> {s.label}
                      </button>
                    ))}
                  </div>

                  {/* Search bar */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    <input
                      value={musicQuery}
                      onChange={(e) => setMusicQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleMusicSearch()}
                      placeholder="Search for background music…"
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 8,
                        border: "1px solid #e5e7eb", fontSize: 13, boxSizing: "border-box",
                      }}
                    />
                    <button
                      onClick={() => handleMusicSearch()}
                      disabled={loadingMusic || !musicQuery.trim()}
                      style={{
                        background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8,
                        padding: "10px 16px", fontWeight: 600, fontSize: 13,
                        cursor: loadingMusic || !musicQuery.trim() ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 6,
                        opacity: !musicQuery.trim() ? 0.5 : 1,
                      }}
                    >
                      {loadingMusic
                        ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                        : <FaSearch style={{ fontSize: 12 }} />}
                      Search
                    </button>
                  </div>

                  {/* Selected track banner */}
                  {selectedTrack && (
                    <div style={{
                      background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 10,
                      padding: "10px 14px", marginBottom: 16,
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <FaCheckCircle style={{ color: "#7c3aed", fontSize: 14, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedTrack.name}
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          {Math.floor(selectedTrack.duration / 60)}:{String(Math.floor(selectedTrack.duration % 60)).padStart(2, "0")}
                          {selectedTrack.username && ` · ${selectedTrack.username}`}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedTrack(null)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "#9ca3af", fontSize: 14, padding: 4,
                        }}
                      ><FaTimes /></button>
                    </div>
                  )}

                  {/* Loading */}
                  {loadingMusic && musicResults.length === 0 && (
                    <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>
                      <Spinner animation="border" size="sm" /> Searching…
                    </div>
                  )}

                  {/* Results */}
                  {musicResults.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16, maxHeight: 320, overflowY: "auto" }}>
                      {musicResults.map((track) => {
                        const isPlaying = playingTrackId === track.id;
                        const isSelected = selectedTrack?.id === track.id;
                        return (
                          <div key={track.id} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px", borderRadius: 10,
                            background: isSelected ? "#f5f3ff" : "#f9fafb",
                            border: isSelected ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
                          }}>
                            <button
                              onClick={() => togglePlayTrack(track)}
                              style={{
                                width: 36, height: 36, borderRadius: "50%",
                                background: isPlaying ? "#7c3aed" : "#e5e7eb",
                                color: isPlaying ? "#fff" : "#374151",
                                border: "none", cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0, fontSize: 13,
                              }}
                            >
                              {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: 2 }} />}
                            </button>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {track.name}
                              </div>
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                                {Math.floor(track.duration / 60)}:{String(Math.floor(track.duration % 60)).padStart(2, "0")}
                                {track.username && ` · ${track.username}`}
                              </div>
                            </div>
                            <button
                              onClick={() => setSelectedTrack(track)}
                              style={{
                                background: isSelected ? "#7c3aed" : "#fff",
                                color: isSelected ? "#fff" : "#7c3aed",
                                border: "1px solid #c4b5fd",
                                borderRadius: 8, padding: "6px 14px",
                                fontWeight: 600, fontSize: 12, cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Empty state */}
                  {!loadingMusic && musicResults.length === 0 && (
                    <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: 20 }}>
                      Search for music above, or continue without music.
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <button
                      onClick={() => { if (audioRef.current) audioRef.current.pause(); setPlayingTrackId(null); setModalStep("review"); }}
                      style={{
                        background: "#f3f4f6", color: "#374151", border: "none",
                        borderRadius: 8, padding: "10px 18px", fontWeight: 600,
                        cursor: "pointer", fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    ><FaArrowLeft style={{ fontSize: 11 }} /> Back</button>
                    <button
                      onClick={() => { if (audioRef.current) audioRef.current.pause(); setPlayingTrackId(null); handleGenerate(); }}
                      style={{
                        flex: 1, background: "#7c3aed", color: "#fff", border: "none",
                        borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                        cursor: "pointer", fontSize: 14,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      }}
                    >
                      <FaMagic style={{ fontSize: 13 }} /> {selectedTrack ? "Build Video" : "Build Video (No Music)"}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step: Generating ── */}
              {modalStep === "generating" && (
                <div>
                  {/* Progress bar */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{
                      width: "100%", height: 6, background: "#e5e7eb",
                      borderRadius: 3, overflow: "hidden",
                    }}>
                      <div style={{
                        height: "100%", width: `${progress}%`,
                        background: "linear-gradient(90deg, #7c3aed, #a78bfa)",
                        borderRadius: 3, transition: "width 0.4s linear",
                      }} />
                    </div>
                    <div style={{
                      fontSize: 12, color: "#6b7280", marginTop: 8, textAlign: "center",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}>
                      <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                      {progressMessage || "Starting…"}
                    </div>
                  </div>

                  {/* Steps timeline */}
                  <div style={{
                    display: "flex", flexDirection: "column", gap: 12,
                    padding: "12px 0",
                  }}>
                    {[
                      { key: "fetch", label: "Fetching listing data", icon: "🔍" },
                      { key: "selecting", label: "AI selecting best images", icon: "🤖" },
                      { key: "text", label: "Generating video text", icon: "✍️" },
                      { key: "building", label: "Building video", icon: "🎬" },
                      { key: "uploading", label: "Uploading video", icon: "☁️" },
                    ].map((step, i) => {
                      const msgLower = (progressMessage || "").toLowerCase();
                      const isActive = msgLower.includes(step.key);
                      const stepOrder = ["fetch", "selecting", "text", "building", "uploading"];
                      const currentIdx = stepOrder.findIndex((s) => msgLower.includes(s));
                      const isDone = currentIdx > i;

                      return (
                        <div key={step.key} style={{
                          display: "flex", alignItems: "center", gap: 10, fontSize: 13,
                          color: isDone ? "#059669" : isActive ? "#7c3aed" : "#9ca3af",
                          fontWeight: isActive ? 600 : 400,
                        }}>
                          <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>
                            {isDone ? <FaCheckCircle style={{ color: "#059669", fontSize: 14 }} /> : step.icon}
                          </span>
                          {step.label}
                          {isActive && !isDone && (
                            <Spinner animation="border" size="sm" style={{ width: 12, height: 12, marginLeft: 4 }} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Slide preview if available */}
                  {slides.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                        Slides Preview
                      </div>
                      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8 }}>
                        {slides.map((s, i) => (
                          <div key={i} style={{
                            flexShrink: 0, width: 120, borderRadius: 8, overflow: "hidden",
                            border: "1px solid #e5e7eb", background: "#f9fafb",
                          }}>
                            <div style={{
                              height: 68, background: `url(${s.image_url}) center/cover no-repeat`,
                              backgroundColor: "#e5e7eb", position: "relative",
                            }}>
                              <div style={{
                                position: "absolute", bottom: 0, left: 0, right: 0,
                                background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                                padding: "8px 6px 4px", color: "#fff", fontSize: 9,
                                fontWeight: 600, lineHeight: 1.2,
                              }}>
                                {s.headline}
                              </div>
                            </div>
                            <div style={{ padding: "4px 6px", fontSize: 9, color: "#6b7280", lineHeight: 1.3 }}>
                              {s.subtitle}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Step: Done ── */}
              {modalStep === "done" && videoUrl && (
                <div style={{ textAlign: "center" }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "#d1fae5", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    margin: "0 auto 16px",
                  }}>
                    <FaCheckCircle style={{ fontSize: 28, color: "#059669" }} />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>
                    Video Generated!
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
                    {resultDetails?.address}
                    {resultDetails?.price && ` — ${resultDetails.price}`}
                  </div>

                  {/* Video player */}
                  <div style={{
                    borderRadius: 12, overflow: "hidden", background: "#000",
                    marginBottom: 20, maxWidth: 640, margin: "0 auto 20px",
                  }}>
                    <video
                      src={videoUrl}
                      controls
                      autoPlay
                      style={{ width: "100%", display: "block" }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      style={{
                        background: "#7c3aed", color: "#fff", border: "none",
                        borderRadius: 8, padding: "10px 24px", fontWeight: 600,
                        cursor: "pointer", fontSize: 14, textDecoration: "none",
                        display: "flex", alignItems: "center", gap: 8,
                      }}
                    >
                      <FaDownload style={{ fontSize: 13 }} /> Download Video
                    </a>
                    <button
                      onClick={closeModal}
                      style={{
                        background: "#f3f4f6", color: "#374151", border: "none",
                        borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                        cursor: "pointer", fontSize: 14,
                      }}
                    >Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Social Post Modal ─── */}
      {socialPostModal.open && (
        <div style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px", borderBottom: "1px solid #e5e7eb", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Social Post
              <button
                onClick={() => setSocialPostModal({ open: false, videoId: null })}
                disabled={socialPostLoading}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>Prompt <span style={{ fontWeight: 400, color: "#9ca3af" }}>(edit to customise the blurb style)</span></label>
                <textarea
                  ref={socialPostPromptRef}
                  value={socialPostPrompt}
                  onChange={e => setSocialPostPrompt(e.target.value)}
                  disabled={socialPostLoading}
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                    overflow: "hidden"
                  }}
                />
              </div>
              {socialPostLoading && !socialPostText ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, minHeight: 120, justifyContent: "center" }}>
                  <Spinner animation="border" />
                  <span style={{ color: "#6b7280", fontSize: 14 }}>Generating social post…</span>
                </div>
              ) : (
                <>
                  {!socialPostLoading && (
                  <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                    Copy this post and share it on your social media — Facebook, Instagram, LinkedIn, or X (Twitter) — to promote your property video and generate leads.
                  </p>
                  )}
                  <textarea
                    ref={socialPostTextRef}
                    value={socialPostText}
                    onChange={e => {
                      setSocialPostText(e.target.value);
                      e.target.style.height = "0px";
                      e.target.style.height = Math.max(200, e.target.scrollHeight) + "px";
                    }}
                    readOnly={socialPostLoading}
                    style={{
                      background: copyFlash ? "#dbeafe" : "#f3f4f6",
                      borderRadius: 8,
                      padding: "16px 18px",
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "#1f2937",
                      width: "100%",
                      boxSizing: "border-box",
                      border: copyFlash ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      transition: "background 0.3s ease, border 0.3s ease",
                      fontFamily: "inherit",
                      resize: "none",
                      overflow: "hidden",
                      minHeight: 200,
                      maxHeight: "none",
                    }}
                  />
                </>
              )}
            </div>
            {fbConnected && showScheduleFields && !socialPostLoading && socialPostText && (
              <div style={{ padding: "12px 28px 0", borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      onClick={handleScheduleVideo}
                      disabled={scheduleSaving || !scheduleDate || !socialPostText.trim() || !fbSelectedPage}
                      style={{
                        background: (!scheduleDate || !socialPostText.trim()) ? "#d1d5db" : "#7c3aed",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "9px 20px",
                        fontWeight: 600,
                        cursor: (!scheduleDate || !socialPostText.trim()) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {scheduleSaving && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                      <FaCalendarAlt />
                      {scheduleSaving ? "Scheduling..." : "Schedule Post"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb", flexShrink: 0, flexWrap: "wrap" }}>
              <button
                onClick={handleRegenerateBlurb}
                disabled={socialPostLoading}
                style={{
                  background: socialPostLoading ? "#a78bfa" : "#7c3aed",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: socialPostLoading ? "not-allowed" : "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: isMobile ? 1 : undefined
                }}
              >
                {socialPostLoading
                  ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                  : <FaMagic style={{ marginRight: 8 }} />}
                Regenerate
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(socialPostText);
                  setCopyFlash(true);
                  setTimeout(() => setCopyFlash(false), 600);
                  showToast("Copied to clipboard!");
                }}
                disabled={socialPostLoading || !socialPostText}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: socialPostLoading || !socialPostText ? "not-allowed" : "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: socialPostLoading || !socialPostText ? 0.5 : 1,
                  flex: isMobile ? 1 : undefined
                }}
              >
                <FaCopy style={{ marginRight: 8 }} /> Copy
              </button>
              {fbConnected && (() => {
                const video = socialPostVideoRef.current;
                const videoId = video?.id ? String(video.id) : "";
                const alreadyPosted = fbPosted.some(p => p.asset_type === "video" && p.asset_id === videoId && p.page_id === fbSelectedPage);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: isMobile ? 1 : undefined }}>
                    {fbPages.length > 1 && (
                      <select
                        value={fbSelectedPage}
                        onChange={e => { setFbSelectedPage(e.target.value); setFbPostMsg(""); }}
                        style={{
                          borderRadius: 6, border: "1px solid #d1d5db", padding: "7px 8px",
                          fontSize: 13, color: "#374151", background: "#f9fafb", maxWidth: 160,
                        }}
                      >
                        {fbPages.map(p => (
                          <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={async () => {
                        setFbPosting(true);
                        setFbPostMsg("");
                        try {
                          const res = await handleTokenAndPostRequest(
                            `${window.api_host}/facebook-post`,
                            { message: socialPostText, link: video?.video_url || '', page_id: fbSelectedPage, asset_type: "video", asset_id: videoId },
                            { "Content-Type": "application/json" }
                          );
                          if (res.data.success) {
                            setFbPostMsg("success");
                            setFbPosted(prev => [...prev, { asset_type: "video", asset_id: videoId, page_id: fbSelectedPage }]);
                            showToast("Posted to Facebook!");
                          } else {
                            setFbPostMsg(res.data.message || "Failed to post.");
                            if (res.data.reconnect) setFbConnected(false);
                          }
                        } catch {
                          setFbPostMsg("Failed to post to Facebook.");
                        } finally {
                          setFbPosting(false);
                        }
                      }}
                      disabled={socialPostLoading || !socialPostText || fbPosting || alreadyPosted}
                      style={{
                        background: alreadyPosted ? "#9ca3af" : fbPostMsg === "success" ? "#059669" : "#1877f2",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 18px",
                        fontWeight: 600,
                        cursor: socialPostLoading || !socialPostText || fbPosting || alreadyPosted ? "not-allowed" : "pointer",
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: socialPostLoading || !socialPostText || fbPosting ? 0.7 : 1,
                        flex: isMobile ? 1 : undefined,
                        transition: "background 0.3s",
                      }}
                    >
                      {fbPosting
                        ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                        : <FaFacebook style={{ marginRight: 8 }} />}
                      {alreadyPosted ? "Already Posted" : fbPostMsg === "success" ? "Posted!" : "Post to Facebook"}
                    </button>
                    <button
                      onClick={() => setShowScheduleFields(v => !v)}
                      disabled={socialPostLoading || !socialPostText}
                      style={{
                        background: showScheduleFields ? "#7c3aed" : "#f3f4f6",
                        color: showScheduleFields ? "#fff" : "#374151",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 14px",
                        fontWeight: 600,
                        cursor: socialPostLoading || !socialPostText ? "not-allowed" : "pointer",
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: socialPostLoading || !socialPostText ? 0.5 : 1,
                        transition: "background 0.3s, color 0.3s",
                      }}
                    >
                      <FaCalendarAlt style={{ marginRight: 6 }} /> Schedule
                    </button>
                  </div>
                );
              })()}
            </div>
            {fbPostMsg && fbPostMsg !== "success" && (
              <div style={{ padding: "0 28px 12px", fontSize: 13, color: "#dc2626" }}>{fbPostMsg}</div>
            )}
          </div>
        </div>
      )}

      {/* ─── Video Player Modal ─── */}
      {playerModal.open && (
        <div
          onClick={() => setPlayerModal({ open: false, url: "", title: "" })}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.75)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#000", borderRadius: 16, overflow: "hidden",
              maxWidth: 800, width: "100%", boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 16px", background: "#111",
            }}>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                {playerModal.title || "Property Video"}
              </span>
              <button
                onClick={() => setPlayerModal({ open: false, url: "", title: "" })}
                style={{
                  background: "none", border: "none", color: "#9ca3af",
                  cursor: "pointer", fontSize: 18, padding: 4,
                }}
              ><FaTimes /></button>
            </div>
            <video
              src={playerModal.url}
              controls
              autoPlay
              style={{ width: "100%", display: "block" }}
            />
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation Modal ─── */}
      {deleteModal.open && (
        <div
          onClick={() => setDeleteModal({ open: false, id: null })}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.35)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, padding: 28, width: 360,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
              Delete Video?
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                style={{
                  background: "#f3f4f6", color: "#374151", border: "none",
                  borderRadius: 8, padding: "8px 18px", fontWeight: 600,
                  cursor: "pointer", fontSize: 13,
                }}
              >Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  background: "#dc2626", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 18px", fontWeight: 600,
                  cursor: deleting ? "not-allowed" : "pointer", fontSize: 13,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {deleting && <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "#dc2626" : "#065f46", color: "#fff",
          padding: "12px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)", zIndex: 2100,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>{toast.type === "error" ? "✕" : "✓"}</span> {toast.message}
        </div>
      )}

      {/* Hidden audio element for music preview */}
      <audio ref={audioRef} onEnded={() => setPlayingTrackId(null)} />
    </div>
  );
}
